/**
 * Office view of a job shift's checklists (status, answers, who and when, corrections) and marking a
 * shift complete. A job shift with a leading hand can't be completed until its end of shift checklist
 * is submitted; the office can complete it anyway with a reason, recorded with who and when.
 */
import { useState } from "react";
import { Notice, useNotice } from "../../components/Notice";
import { BRAND } from "../../lib/config";
import { fmtDate } from "../../lib/format";
import type { ChecklistKind, ShiftChecklistRow } from "../../lib/supabase";
import { AnswerList, CHECKLIST_TITLE, statusChip } from "./Checklist";
import { completeWithoutChecklist, currentRow } from "./field-data";

const configuredKinds = (): ChecklistKind[] => (["start", "end"] as const).filter((k) => (BRAND.field?.checklists[k].length ?? 0) > 0);

export function ShiftChecklistStatus({ bookingId, rows, names }: { bookingId: string; rows: ShiftChecklistRow[]; names: Map<string, string> }) {
  const [open, setOpen] = useState(false);
  const kinds = configuredKinds();
  if (!kinds.length) return null;
  const current = Object.fromEntries(kinds.map((k) => [k, currentRow(rows, bookingId, k)])) as Record<ChecklistKind, ShiftChecklistRow | null>;
  const status = (r: ShiftChecklistRow | null) => (!r ? "not_started" : r.submitted_at ? "complete" : "started") as "not_started" | "started" | "complete";

  return (
    <span className="stack" style={{ gap: 6, marginTop: 6 }}>
      {kinds.map((k) => <span key={k} className="small">{k === "start" ? "Start" : "End"}: {statusChip(status(current[k]))}</span>)}
      {kinds.some((k) => current[k]) && (
        <button type="button" className="btn btn--outline btn--xs" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? "Hide checklists" : "Show checklists"}</button>
      )}
      {open && kinds.map((k) => {
        const r = current[k];
        if (!r) return null;
        const older = rows.filter((x) => x.booking_id === bookingId && x.kind === k && x.id !== r.id && x.submitted_at);
        return (
          <span key={k} className="stack" style={{ gap: 6 }}>
            <span className="strong">{CHECKLIST_TITLE[k]}</span>
            <span className="small muted">
              {r.submitted_at ? `Submitted ${fmtDate(r.submitted_at)}` : `Started ${fmtDate(r.started_at)}, last saved ${fmtDate(r.updated_at)}`}
              {r.crew_member_id ? ` by ${names.get(r.crew_member_id) ?? "the leading hand"}` : ""}
              {r.replaces ? `. Correction: ${r.correction_reason}` : ""}
            </span>
            {r.context.permit && r.context.permit.withinHours !== null && (
              <span className="small">{r.context.permit.withinHours ? "Shift start was inside the permit hours." : "Shift start was outside the permit hours."}</span>
            )}
            <AnswerList items={r.items} />
            {older.length > 0 && <span className="small muted">{older.length === 1 ? "1 earlier version" : `${older.length} earlier versions`} kept for the record.</span>}
          </span>
        );
      })}
    </span>
  );
}

/** Shown when completing a shift is blocked because the end of shift checklist is not in. */
export function CompleteOverride({ bookingId, onDone, onCancel }: { bookingId: string; onDone: () => void; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  async function go() {
    setBusy(true);
    try {
      await completeWithoutChecklist(bookingId, reason);
      onDone();
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  }
  return (
    <div className="card card--alert" role="alertdialog" aria-labelledby={`cmp-${bookingId}`}>
      <div className="card__head"><h2 className="card__title" id={`cmp-${bookingId}`}>End of shift checklist not in</h2></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <p>The leading hand has not submitted the end of shift checklist, so this can't be marked complete yet. Ask them to submit it from their phone, or complete it anyway with a reason. The reason is recorded with your name.</p>
        <label className="field">
          <span className="field__label">Reason for completing without the checklist</span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Paper checklist on file, phone was flat" />
        </label>
        <div className="form-actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy || reason.trim().length < 5} aria-busy={busy} onClick={() => void go()}>{busy ? "Saving" : "Complete anyway"}</button>
          <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={onCancel}>Leave it open</button>
        </div>
      </div>
    </div>
  );
}
